import { $api, fetchClient } from '@/api/typedClient'
import { useTenantStore } from '@/stores/tenantStore'
import type { components } from '@/types/api/v1'
import {
  type Edge,
  type Node,
  ReactFlow,
  ReactFlowProvider,
  useUpdateNodeInternals,
} from '@xyflow/react'
import { Empty, Switch } from 'antd'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import '@xyflow/react/dist/style.css'
import { AuditEdge } from './AuditEdge'
import { AuditGroupNode } from './AuditGroupNode'
import { AuditNode } from './AuditNode'
import { AuditRowNode } from './AuditRowNode'
import {
  DEFAULT_WIDTH,
  META_W,
  META_X,
  buildFlow,
  columnGeometry,
} from './auditLayout'
import {
  AuditNavContext,
  AuditToggleContext,
  type ReferenceLink,
} from './auditToggleContext'

const REFERENCE_DATA_PATH = '/integration-management/reference-data'

type TranslationAudit = components['schemas']['TranslationAudit']

function ColumnHeader({
  x,
  w,
  label,
  sub,
}: {
  x: number
  w: number
  label: string
  sub?: string
}) {
  return (
    <div
      style={{
        position: 'absolute',
        left: x,
        width: w,
        top: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <span
        title={label}
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'hsl(var(--muted-foreground))',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {label}
      </span>
      {sub ? (
        <span
          title={sub}
          style={{
            fontSize: 10,
            color: 'hsl(var(--muted-foreground))',
            opacity: 0.75,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {sub}
        </span>
      ) : null}
    </div>
  )
}

const nodeTypes = {
  auditGroup: AuditGroupNode,
  auditRow: AuditRowNode,
  auditValue: AuditNode,
}

const edgeTypes = {
  auditEdge: AuditEdge,
}

interface AuditFlowProps {
  audit: TranslationAudit
  // Falls back to routing from the log when the audit record has none.
  routing?: TranslationAudit['routing']
}

// ReactFlow reads handle bounds from the DOM to route edges. When the flow first
// mounts (racing the modal open + font load) those bounds can be measured stale,
// leaving edges detached from their nodes. Re-poking node internals after mount
// and once fonts settle forces the edges to re-attach to the box borders.
function Flow({ nodes, edges }: { nodes: Node[]; edges: Edge[] }) {
  const updateNodeInternals = useUpdateNodeInternals()

  useEffect(() => {
    const ids = nodes.map((n) => n.id)
    const refresh = () => updateNodeInternals(ids)
    refresh()
    const raf = requestAnimationFrame(refresh)
    const t1 = setTimeout(refresh, 150)
    const t2 = setTimeout(refresh, 400)
    let cancelled = false
    document.fonts?.ready.then(() => {
      if (!cancelled) refresh()
    })
    return () => {
      cancelled = true
      cancelAnimationFrame(raf)
      clearTimeout(t1)
      clearTimeout(t2)
    }
  }, [nodes, updateNodeInternals])

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      nodeTypes={nodeTypes}
      edgeTypes={edgeTypes}
      defaultViewport={{ x: 0, y: 0, zoom: 1 }}
      minZoom={1}
      maxZoom={1}
      fitView={false}
      panOnScroll={false}
      panOnDrag={false}
      zoomOnScroll={false}
      zoomOnPinch={false}
      zoomOnDoubleClick={false}
      preventScrolling={false}
      nodesDraggable={false}
      nodesConnectable={false}
      elementsSelectable={false}
      proOptions={{ hideAttribution: true }}
    />
  )
}

export default function AuditFlow({
  audit,
  routing: routingProp,
}: AuditFlowProps) {
  const [showTechnical, setShowTechnical] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(DEFAULT_WIDTH)
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(
    () => new Set(),
  )

  const toggleGroup = useCallback((groupKey: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(groupKey)) next.delete(groupKey)
      else next.add(groupKey)
      return next
    })
  }, [])

  const { selectedTenant, selectTenant } = useTenantStore()
  const { data: tenants } = $api.useQuery('post', '/meta/tenant/all', {})

  // Only the column whose tenant is currently selected gets a reference-data
  // link (master data is tenant-agnostic and handled separately).
  const routing = audit.routing ?? routingProp
  const sourceLinks = useMemo(
    () => ({
      origin: routing?.origin_tenant_name === selectedTenant?.name,
      destination: routing?.destination_tenant_name === selectedTenant?.name,
    }),
    [routing, selectedTenant],
  )

  const openReference = useCallback(
    (link: ReferenceLink) => {
      const referenceUrl = (object: string | undefined) => {
        const params = new URLSearchParams()
        if (object) params.set('object', object)
        if (link.goid) params.set('go_ids', link.goid)
        if (link.sourceId) params.set('source_ids', link.sourceId)
        return `${REFERENCE_DATA_PATH}?${params.toString()}`
      }

      // Master data is tenant-agnostic and its type is encoded in the goid, so
      // open directly.
      if (link.tenantSide === 'master') {
        window.open(referenceUrl(link.object), '_blank')
        return
      }

      // Origin/destination values live in their tenant; switch to it (persists
      // to localStorage, which the new tab reads on load) so the source-id
      // filter resolves.
      const tenantName =
        link.tenantSide === 'origin'
          ? routing?.origin_tenant_name
          : routing?.destination_tenant_name
      if (tenantName && tenants) {
        const match = tenants.find((t) => t.name === tenantName)
        if (match) selectTenant(match)
      }

      // Without a mapping id we can only use the field-inferred fallback type.
      if (!link.mappingId) {
        window.open(referenceUrl(link.object), '_blank')
        return
      }

      // Open the tab synchronously (in the click gesture, so it isn't blocked),
      // then resolve the mapping's true type from the backend and redirect it.
      const win = window.open('', '_blank')
      fetchClient
        .GET('/crossroads/mappings/{id}', {
          params: {
            path: { id: link.mappingId },
            header: tenantName ? { 'X-Tenant-Name': tenantName } : undefined,
          },
        })
        .then(({ data }) => {
          const type = data?.type
          const object = type && type !== 'tank' ? type : link.object
          const url = referenceUrl(object)
          if (win) win.location.href = url
          else window.open(url, '_blank')
        })
        .catch(() => {
          const url = referenceUrl(link.object)
          if (win) win.location.href = url
        })
    },
    [routing, tenants, selectTenant],
  )

  // Track the scroll container width so the grid fills whatever the modal gives
  // it. Only the horizontal columns depend on this; row heights are unaffected.
  useEffect(() => {
    const el = scrollRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => {
      setWidth(Math.max(DEFAULT_WIDTH, el.clientWidth))
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const { nodes, edges, totalHeight } = useMemo(
    () =>
      buildFlow(
        audit.details ?? [],
        showTechnical,
        width,
        collapsed,
        sourceLinks,
      ),
    [audit, showTechnical, width, collapsed, sourceLinks],
  )

  const cols = columnGeometry(width)
  const originLabel = routing?.origin_tenant_name || 'Source'
  const originSub = routing?.origin_source_system || undefined
  const destLabel = routing?.destination_tenant_name || 'Destination'
  const destSub = routing?.destination_source_system || undefined

  if (!nodes.length) {
    return <Empty description='No fields in this audit' />
  }

  return (
    <AuditToggleContext.Provider value={toggleGroup}>
      <AuditNavContext.Provider value={openReference}>
        <div
          style={{
            height: '100%',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              flex: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 8,
              padding: '4px 8px 10px',
            }}
          >
            <span
              style={{ fontSize: 12, color: 'hsl(var(--muted-foreground))' }}
            >
              Show IDs
            </span>
            <Switch
              size='small'
              checked={showTechnical}
              onChange={setShowTechnical}
            />
          </div>
          <div
            style={{
              flex: 'none',
              position: 'relative',
              height: 36,
              borderBottom: '1px solid hsl(var(--border))',
            }}
          >
            <ColumnHeader x={META_X} w={META_W} label='Field' />
            <ColumnHeader
              x={cols.originX}
              w={cols.nodeW}
              label={originLabel}
              sub={originSub}
            />
            <ColumnHeader
              x={cols.masterX}
              w={cols.nodeW}
              label='Crossroads Master Data'
            />
            <ColumnHeader
              x={cols.destX}
              w={cols.nodeW}
              label={destLabel}
              sub={destSub}
            />
          </div>
          <div
            ref={scrollRef}
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
          >
            <div
              style={{
                position: 'relative',
                width: '100%',
                height: totalHeight,
              }}
            >
              <ReactFlowProvider>
                <Flow nodes={nodes as Node[]} edges={edges as Edge[]} />
              </ReactFlowProvider>
            </div>
          </div>
        </div>
      </AuditNavContext.Provider>
    </AuditToggleContext.Provider>
  )
}
